import { useState, useEffect, useContext, useMemo } from "react";
import { Grid, TextField, Button, Select, MenuItem, FormControl } from "@mui/material";
import styles from "./styles.module.css";
import {
  STORAGE_KEYS,
  CURRENCY_OPTIONS,
  TOGGLE_OPTIONS,
  SELECT_FIELD_CONFIGS,
  INVOICE_DISPLAY_OPTIONS,
  STOCK_CHECK_OPTIONS,
  ADD_TO_CART_OPTIONS,
  KOT_SPLIT_OPTIONS,
  DEFAULT_INVOICE_ACTION_OPTIONS,
  TERMINAL_CONFIG_KEYS,
  APP_SETTING_KEYS,
} from "const";
import ShopContext from "context/ShopContext";
import TerminalService from "services/TerminalService";
import toast from "react-hot-toast";
import CustomFormSwitch from "components/utils/CustomFields/CustomFormSwitch";
import ShopSettingsService from "services/ShopService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import * as TerminalHelper from "helpers/TermialHelper";
import * as AppSettingsHelper from "helpers/AppSettingsHelper";
import * as SalespersonHelper from "helpers/SalespersonHelper";
import { TerminalContext } from "context/TerminalContext";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import BillNumberSetUpPopup from "components/popups/BillNumberSetUpPopup/BillNumberSetUpPopup";

export default function BasicSettings() {
  const { selectedShop, setSelectedShop, hasFullKotValidity } = useContext(ShopContext);
  const { setTerminalSettings, fetchTerminalSettings, settingsCache } = useContext(TerminalContext);

  const [settings, setSettings] = useState({
    showToken: false,
    paymentPage: false,
    orderFullPayment: false,
    displayAwaitingDispatchOrders: false,
    salespersonLogin: false,
    printOnPayment: false,
    paymentModeOnInvoice: false,
    printMRP: false,
    printHSN: false,
    counterFoil: false,
    printTotalSave: false,
    amountFractions: false,
    // confirmWeight: false,
    advancedQty: false,
    tableSelection: false,
    showItemsOnKotWhenTableChange: false,
    printKOTCategory: false,
    kotWithInvoice: false,
    consolidatedKOT: false,
    autoReceiveFullAmount: false,
    showItemChargesOnInvoice: false,
    kotWithInvoiceIp: "",
    consolidatedKOTIp: "",
    printPaymentQrOnInvoice: false,
  });
  const [invoicePrefix, setInvoicePrefix] = useState("");
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState("");
  const [showBillNoSetupPopup, setShowBillNoSetupPopup] = useState(false);
  const [formSelects, setFormSelects] = useState({
    invoiceDisplayRate: "",
    preventNegativeStock: "",
    addToCart: "",
    kotSplitOptions: KOT_SPLIT_OPTIONS.KOT_SPLIT_BY_KITCHEN_NAME.value,
    defaultInvoiceAction: "",
  });

  const [currency, setCurrency] = useState({
    symbol: "",
    name: "",
    fraction: "",
  });

  const [loading, setLoading] = useState(false);

  const toggleMapping = useMemo(
    () => ({
      showToken: "show_token_number",
      paymentPage: "show_payment_page",
      orderFullPayment: "order_with_full_payment_only",
      displayAwaitingDispatchOrders: "display_awaiting_dispatch_orders",
      salespersonLogin: "salesperson_login",
      printOnPayment: "print_invoice_on_payment_update",
      paymentModeOnInvoice: "show_payment_mode_on_invoice",
      printMRP: "show_mrp_column_on_invoice",
      amountFractions: "amount_with_three_fraction",
      printTotalSave: "print_total_save_on_invoice",
      counterFoil: "counter_foil_with_invoice",
      advancedQty: "advanced_quantity_control",
      tableSelection: "enable_table_selection_pos",
      showItemsOnKotWhenTableChange: "show_items_on_kot_when_table_change",
      kotWithInvoice: "kot_with_bill",
      consolidatedKOT: "consolidated_kot",
      autoReceiveFullAmount: "auto_receive_full_amount",
      printHSN: "print_hsn",
      showItemChargesOnInvoice: "show_item_charges_on_invoice",
      printPaymentQrOnInvoice: "print_payment_qr_on_invoice",
      // confirmWeight: "confirm_bluetooth_scale_weight",
    }),
    [],
  );

  const handleCurrencySelect = (event) => {
    const selectedSymbol = event.target.value;
    const selected = CURRENCY_OPTIONS.find((c) => c.symbol === selectedSymbol);

    setCurrency((prev) => ({
      ...prev,
      symbol: selected.symbol,
      name: selected.name,
      fraction: selected.fraction,
    }));
  };

  async function fetchOnlineTerminalSettings(fetchTerminalSettings, toggleMapping, setSettings) {
    const res = await fetchTerminalSettings();
    if (!res) return;

    TerminalHelper.saveTerminalSettings({ upi_id: res.upi_id || "" });

    const configObj = res.config || {};
    const newSettings = Object.fromEntries(
      Object.entries(toggleMapping).map(([uiKey, apiKey]) => {
        const val = configObj[apiKey] ?? res[apiKey];
        return [uiKey, !!val];
      }),
    );

    setSettings({
      ...newSettings,
      kotWithInvoiceIp: res.kot_with_invoice_printer_ip || "",
      consolidatedKOTIp: res.consolidated_kot_printer_ip || "",
    });
  }

  function loadOfflineTerminalSettings(setSettings) {
    const localTerminalSettings = TerminalHelper.getTerminalSettings();
    const newSettings = {
      showToken: !!localTerminalSettings.showTokenNumber,
      paymentPage: !!localTerminalSettings.showPaymentPage,
      orderFullPayment: !!localTerminalSettings.orderFullPayment,
      displayAwaitingDispatchOrders: !!localTerminalSettings.displayAwaitingDispatchOrders,
      salespersonLogin: localTerminalSettings.salespersonLogin ?? true,
      printOnPayment: !!localTerminalSettings.printOnPayment,
      paymentModeOnInvoice: !!localTerminalSettings.paymentModeOnInvoice,
      printMRP: !!localTerminalSettings.printMrpOnInvoice,
      printHSN: !!localTerminalSettings.printHsnOnInvoice,
      counterFoil: !!localTerminalSettings.counterFoil,
      printTotalSave: !!localTerminalSettings.printTotalSave,
      amountFractions: !!localTerminalSettings.amountWithThreeFraction,
      advancedQty: !!localTerminalSettings.advancedQty,
      tableSelection: !!localTerminalSettings.tableSelection,
      showItemsOnKotWhenTableChange: !!localTerminalSettings.showItemsOnKotWhenTableChange,
      kotWithInvoice: !!localTerminalSettings.kotWithInvoice,
      consolidatedKOT: !!localTerminalSettings.consolidatedKOT,
      autoReceiveFullAmount: !!localTerminalSettings.autoReceiveFullAmount,
      showItemChargesOnInvoice: !!localTerminalSettings.showItemChargesOnInvoice,
      kotWithInvoiceIp: localTerminalSettings.kotWithInvoiceIp || "",
      consolidatedKOTIp: localTerminalSettings.consolidatedKOTIp || "",
      printPaymentQrOnInvoice: !!localTerminalSettings.printPaymentQrOnInvoice,
    };
    setSettings(newSettings);
  }

  function buildTerminalSettingsUpdates(formSelects, settings) {
    return {
      invoiceDisplayRate: formSelects.invoiceDisplayRate,
      preventNegativeStock: formSelects.preventNegativeStock,
      addToCart: formSelects.addToCart,
      kotSplitOptions: formSelects.kotSplitOptions,
      defaultInvoiceAction: formSelects.defaultInvoiceAction,
      showTokenNumber: settings.showToken,
      amountWithThreeFraction: settings.amountFractions,
      printMrpOnInvoice: settings.printMRP,
      printHsnOnInvoice: settings.printHSN,
      paymentModeOnInvoice: settings.paymentModeOnInvoice,
      printTotalSave: settings.printTotalSave,
      kotWithInvoice: settings.kotWithInvoice,
      kotWithInvoiceIp: settings.kotWithInvoiceIp,
      consolidatedKOT: settings.consolidatedKOT,
      consolidatedKOTIp: settings.consolidatedKOTIp,
      counterFoil: settings.counterFoil,
      orderFullPayment: settings.orderFullPayment,
      displayAwaitingDispatchOrders: settings.displayAwaitingDispatchOrders,
      autoReceiveFullAmount: settings.autoReceiveFullAmount,
      printOnPayment: settings.printOnPayment,
      showItemChargesOnInvoice: settings.showItemChargesOnInvoice,
      printPaymentQrOnInvoice: settings.printPaymentQrOnInvoice,
      showItemsOnKotWhenTableChange: settings.showItemsOnKotWhenTableChange,
      salespersonLogin: settings.salespersonLogin,
    };
  }

  useEffect(() => {
    if (!selectedShop) return;

    const terminalSettings = TerminalHelper.getTerminalSettings();

    setFormSelects({
      invoiceDisplayRate: terminalSettings.invoiceDisplayRate || INVOICE_DISPLAY_OPTIONS.ACTUAL_RATE.value,
      preventNegativeStock: terminalSettings.preventNegativeStock || STOCK_CHECK_OPTIONS.ALLOW_NEGATIVE_STOCK.value,
      addToCart: terminalSettings.addToCart || ADD_TO_CART_OPTIONS.APPEND.value,
      kotSplitOptions: terminalSettings.kotSplitOptions || KOT_SPLIT_OPTIONS.KOT_SPLIT_BY_KITCHEN_NAME.value,
      defaultInvoiceAction: terminalSettings.defaultInvoiceAction || DEFAULT_INVOICE_ACTION_OPTIONS.PRINT.value,
    });

    setCurrency({
      symbol: selectedShop.currency_symbol,
      name: selectedShop.currency_name,
      fraction: selectedShop.currency_fraction_name,
    });

    const loadSettings = async () => {
      setLoading(true);
      try {
        const billNumberPrefixFromDb = (await AppSettingsHelper.getSetting(APP_SETTING_KEYS.BILL_NUMBER_PREFIX)) || "";
        const billNumberSufffixFromDb = (await AppSettingsHelper.getSetting(APP_SETTING_KEYS.NEXT_BILL_NO)) ?? "";

        setInvoicePrefix(billNumberPrefixFromDb);
        setNextInvoiceNumber(
          billNumberSufffixFromDb !== "" && billNumberSufffixFromDb !== null ? String(billNumberSufffixFromDb) : "",
        );

        await fetchOnlineTerminalSettings(fetchTerminalSettings, toggleMapping, setSettings);
      } catch (err) {
        console.warn("Failed to fetch terminal settings from server (offline):", err);
        loadOfflineTerminalSettings(setSettings);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [selectedShop, fetchTerminalSettings, toggleMapping]);

  const handleSelectChange = (key) => (event) => {
    setFormSelects((prev) => ({
      ...prev,
      [key]: event.target.value,
    }));
  };

  const handleToggle = (name) => {
    setSettings((prev) => ({ ...prev, [name]: !prev[name] }));
  };
  const handleIpChange = (name, value) => {
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  function buildUpdatedTerminalConfig(toggleMapping, settings, settingsCache) {
    const currentConfig = (settingsCache && settingsCache.config) || {};
    const updatedConfig = { ...currentConfig };
    const updatedData = {};

    Object.entries(toggleMapping).forEach(([uiKey, apiKey]) => {
      if (TERMINAL_CONFIG_KEYS.includes(uiKey)) {
        updatedConfig[apiKey] = settings[uiKey];
      } else {
        updatedData[apiKey] = settings[uiKey];
      }
    });

    updatedData.kot_with_invoice_printer_ip = settings.kotWithInvoice ? settings.kotWithInvoiceIp : "";
    updatedData.consolidated_kot_printer_ip = settings.consolidatedKOT ? settings.consolidatedKOTIp : "";
    updatedData.config = updatedConfig;
    return updatedData;
  }

  function updateCachedShopProfile(shopPayload) {
    const shopProfileStr = localStorage.getItem(STORAGE_KEYS.SHOP_PROFILE);
    if (shopProfileStr) {
      try {
        const currentProfile = JSON.parse(shopProfileStr);
        const updatedProfile = { ...currentProfile, ...shopPayload };
        localStorage.setItem(STORAGE_KEYS.SHOP_PROFILE, JSON.stringify(updatedProfile));
      } catch (e) {
        console.error("Failed to update cached shop profile:", e);
      }
    }
  }

  const handleUpdateTerminalSettings = async () => {
    setLoading(true);

    const shopPayload = {
      invoice_display: formSelects.invoiceDisplayRate,
      prevent_negative_stock: formSelects.preventNegativeStock,
      add_to_cart_option: formSelects.addToCart,
      currency_symbol: currency.symbol,
      currency_name: currency.name,
      currency_fraction_name: currency.fraction,
    };

    try {
      const terminalId = localStorage.getItem(STORAGE_KEYS.TERMINAL_ID);
      if (!terminalId) return;

      const updatedData = buildUpdatedTerminalConfig(toggleMapping, settings, settingsCache);
      const terminalSettingsUpdates = buildTerminalSettingsUpdates(formSelects, settings);

      setTerminalSettings((prev) => {
        const updatedCache = prev.settingsCache ? { ...prev.settingsCache, ...updatedData } : null;
        return {
          ...prev,
          ...terminalSettingsUpdates,
          settingsCache: updatedCache,
        };
      });
      TerminalHelper.saveTerminalSettings(terminalSettingsUpdates);
      if (!settings.salespersonLogin) {
        SalespersonHelper.clearSalespersonSession();
      }

      setSelectedShop((prev) => ({ ...prev, ...shopPayload }));
      updateCachedShopProfile(shopPayload);

      try {
        await TerminalService.updateTerminalConfig(terminalId, updatedData);
        toast.success("Settings updated successfully");
      } catch (err) {
        console.warn("Failed to sync terminal settings to server (offline):", err);
        toast.success("Settings saved locally (offline mode)");
      }
    } catch (localErr) {
      toast.error("Error saving settings locally");
      console.error(localErr);
    } finally {
      setLoading(false);
    }

    try {
      const updatedShop = await ShopSettingsService.updateShopProfile(shopPayload);
      if (updatedShop && typeof updatedShop === "object") {
        setSelectedShop(updatedShop);
        localStorage.setItem(STORAGE_KEYS.SHOP_PROFILE, JSON.stringify(updatedShop));
      }
    } catch (err) {
      console.warn("Failed to sync shop profile to server:", err);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.scrollableArea}>
        <Grid container className={styles.invoiceSection} alignItems="center">
          {loading ? (
            <SkeletonLoading isOpen={loading} count={1} height={45} xs={12} md={12} sm={12} lg={12} />
          ) : (
            <>
              <Grid item xs={12} md={4} className={styles.invoiceContent}>
                <h2 className={styles.label}>Invoice number prefix :</h2>
                <h3 className={styles.value}>{invoicePrefix || "-"}</h3>
              </Grid>

              <Grid item xs={12} md={4} className={styles.invoiceContent}>
                <h2 className={styles.label}>Next invoice number :</h2>
                <h3 className={styles.value}>{nextInvoiceNumber || "-"}</h3>
              </Grid>

              <Grid item xs={12} md={4} className={styles.invoiceContent} style={{ justifyContent: "flex-end" }}>
                <Button className={styles.button} variant="outlined" onClick={() => setShowBillNoSetupPopup(true)}>
                  Edit
                </Button>
              </Grid>
            </>
          )}
        </Grid>

        {showBillNoSetupPopup && (
          <BillNumberSetUpPopup
            isOpen={showBillNoSetupPopup}
            onClose={() => setShowBillNoSetupPopup(false)}
            onSuccess={async ({ prefix, suffix }) => {
              await AppSettingsHelper.saveSetting(APP_SETTING_KEYS.BILL_NUMBER_PREFIX, prefix);
              await AppSettingsHelper.saveNextBillNumber(suffix);
              setInvoicePrefix(prefix);
              setNextInvoiceNumber(String(suffix));
              setShowBillNoSetupPopup(false);
              toast.success("Invoice settings updated successfully");
            }}
          />
        )}

        {/* Toggle Options */}
        <Grid container style={{ marginBottom: "15px" }}>
          {loading ? (
            <SkeletonLoading isOpen={loading} count={TOGGLE_OPTIONS.length} height={58} xs={6} md={6} sm={6} lg={6} />
          ) : (
            TOGGLE_OPTIONS.filter((item) => {
              if (item.key === "tableSelection" && !hasFullKotValidity) return false;
              return true;
            }).map((item) => (
              <Grid item xs={12} sm={6} key={item.key}>
                <div className={styles.toggleItem}>
                  <div className={styles.toggleText}>
                    <h3>{item.label}</h3>
                    <h4>{item.description}</h4>
                  </div>
                  <CustomFormSwitch checked={settings[item.key]} onChange={() => handleToggle(item.key)} />
                </div>
                {item.hasIp && settings[item.key] && (
                  <div className={styles.ipInputSection}>
                    <TextField
                      label={item.key === "kotWithInvoice" ? "KOT Printer IP" : "Consolidated KOT Printer IP"}
                      size="small"
                      fullWidth
                      value={item.key === "kotWithInvoice" ? settings.kotWithInvoiceIp : settings.consolidatedKOTIp}
                      onChange={(e) =>
                        handleIpChange(
                          item.key === "kotWithInvoice" ? "kotWithInvoiceIp" : "consolidatedKOTIp",
                          e.target.value,
                        )
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                        },
                      }}
                    />
                  </div>
                )}
              </Grid>
            ))
          )}
        </Grid>

        {/* Bottom Fields */}
        <Grid container className={styles.bottomFields}>
          {SELECT_FIELD_CONFIGS.map((field) => (
            <Grid item xs={12} md={3} className={styles.formControl} key={field.key}>
              <h3>{field.label}</h3>
              <FormControl
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#f9f9f9",
                    "& fieldset": { borderColor: "#ccc" },
                    "&:hover fieldset": { borderColor: "#888" },
                    "&.Mui-focused fieldset": { borderColor: "#1976d2", borderWidth: "2px" },
                  },
                  "& .MuiSelect-select": { padding: "10px 14px", fontSize: "14px" },
                }}
              >
                <Select
                  value={formSelects[field.key]}
                  onChange={handleSelectChange(field.key)}
                  IconComponent={ExpandMoreIcon}
                >
                  {Object.values(field.options).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}

          <Grid item xs={12} md={3} className={styles.formControl}>
            <h3>Currency symbol</h3>
            <FormControl
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#f9f9f9",
                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#888",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                    borderWidth: "2px",
                  },
                },
                "& .MuiSelect-select": {
                  padding: "10px 14px",
                  fontSize: "14px",
                },
              }}
            >
              <Select value={currency.symbol} onChange={handleCurrencySelect} IconComponent={ExpandMoreIcon}>
                {CURRENCY_OPTIONS.map((option) => (
                  <MenuItem key={option.symbol} value={option.symbol}>
                    {option.symbol} — {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} className={styles.formControl}>
            <h3>Currency name</h3>
            <TextField
              placeholder="Currency name"
              fullWidth
              value={currency.name}
              disabled
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#f9f9f9",
                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#888",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "10px 14px",
                  fontSize: "14px",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={3} className={styles.formControl}>
            <h3>Currency fraction name</h3>
            <TextField
              placeholder="Currency fraction name"
              fullWidth
              value={currency.fraction}
              disabled
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#f9f9f9",
                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#888",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "10px 14px",
                  fontSize: "14px",
                },
              }}
            />
          </Grid>
        </Grid>
      </div>

      {/* Buttons */}
      <div className={styles.buttonContainer}>
        <ButtonGray className={styles.buttonGray}>Cancel</ButtonGray>
        <ButtonRed className={styles.buttonRed} onClick={handleUpdateTerminalSettings}>
          Update
        </ButtonRed>
      </div>
    </div>
  );
}
